// useAciSipConnection.js
import { ref, computed, markRaw, onUnmounted } from 'vue';
import AciTelephonyAPI from './index';
import AciSipClient from './AciSipClient';
import { useCallHistory } from './useCallHistory';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useAlert } from 'dashboard/composables';
import CallPermissionsAPI from 'dashboard/api/whatsappSip/callPermissions';
import store from 'dashboard/store';
import {
  notifyRegistrationEvent,
  notifyCallEvent,
  notifyIncomingRejectedWhilePaused,
} from 'dashboard/composables/useSipStatusToast';

const TOAST_LABEL = 'SIP';

// Singleton state - stored on window to survive HMR (Vite hot module replacement).
// Without this, HMR creates a new module with new refs, but the old AciSipClient
// instance still holds callbacks to the old refs, causing state desync.
function getHmrSafeState() {
  if (!window.__aciSipState) {
    window.__aciSipState = {
      client: ref(null),
      isRegistered: ref(false),
      isConnecting: ref(false),
      credentials: ref(null),
      connectionError: ref(null),
      callState: ref('idle'),
      callDirection: ref(null),
      callNumber: ref(null),
      remoteDisplayName: ref(null),
      callStartTime: ref(null),
      isMuted: ref(false),
      isOnHold: ref(false),
      currentSession: null,
      isConsultationActive: ref(false),
      consultationState: ref('idle'),
      consultationTarget: ref(null),
      callMetadata: ref({ name: null, thumbnail: null }),
      lastCallEndedAt: 0,
      callHistoryInstance: null,
    };
  }
  return window.__aciSipState;
}

const _state = getHmrSafeState();

const client = _state.client;
const isRegistered = _state.isRegistered;
const isConnecting = _state.isConnecting;
const credentials = _state.credentials;
const connectionError = _state.connectionError;

// Call state
const callState = _state.callState;
const callDirection = _state.callDirection;
const callNumber = _state.callNumber;
const remoteDisplayName = _state.remoteDisplayName;
const callStartTime = _state.callStartTime;
const isMuted = _state.isMuted;
const isOnHold = _state.isOnHold;

// Cooldown after call ends (prevents ghost re-dial from Vue re-render)
const MAKE_CALL_COOLDOWN_MS = 1500;

// Consultation (attended transfer) state
const isConsultationActive = _state.isConsultationActive;
const consultationState = _state.consultationState;
const consultationTarget = _state.consultationTarget;

// Call metadata (para historico)
const callMetadata = _state.callMetadata;

// Mutable state (not refs, accessed via _state)
// currentSession and lastCallEndedAt are accessed via _state directly (mutable, not refs)

// Inicializa o composable de historico (singleton)
function getCallHistory() {
  if (!_state.callHistoryInstance) {
    _state.callHistoryInstance = useCallHistory();
  }
  return _state.callHistoryInstance;
}

/**
 * Define metadados adicionais da chamada (nome, thumbnail)
 * Chamado pelos componentes que tem acesso a essas informacoes
 */
function setCallMetadata(metadata) {
  callMetadata.value = { ...callMetadata.value, ...metadata };
}

/**
 * Salva a chamada atual no historico
 */
function saveCallToHistory() {
  // Nao salva se nao tinha chamada
  if (!callNumber.value) return;

  const { addToHistory } = getCallHistory();
  const duration = callStartTime.value
    ? Math.floor((new Date() - callStartTime.value) / 1000)
    : 0;

  addToHistory({
    number: callNumber.value,
    duration,
    type: callDirection.value === 'incoming' ? 'incoming' : 'outgoing',
    name: callMetadata.value.name,
    thumbnail: callMetadata.value.thumbnail,
  });

  // Limpa metadados
  callMetadata.value = { name: null, thumbnail: null };
}

/**
 * Normaliza número de telefone brasileiro ou ramal
 */
export function normalizePhoneOrExtension(phoneNumber) {
  if (!phoneNumber) return null;

  const raw = String(phoneNumber).trim();

  // Feature codes do Asterisk (começam com * ou #) passam direto, só remove espaços/hífens
  if (/^[*#]/.test(raw)) {
    const featureCode = raw.replace(/[^\d*#+]/g, '');
    return featureCode || null;
  }

  // Remove tudo exceto dígitos
  let clean = raw.replace(/\D/g, '');

  if (!clean) return null;

  // Ramal ou atalho de fila sem prefixo (o ACI aceita 2-6 dígitos, ex.: 9029,
  // 123456) — nenhum telefone tem até 6 dígitos, então passa direto.
  if (clean.length <= 6) {
    return clean;
  }

  // Remove código do país se presente
  clean = clean.replace(/^55/, '');

  // Número local sem DDD (8 dígitos fixo, 9 dígitos celular) — passa direto para o Asterisk
  if (clean.length >= 8 && clean.length <= 9) {
    return clean;
  }

  // Valida tamanho para telefone brasileiro com DDD
  if (clean.length < 10 || clean.length > 11) {
    console.warn('[useAciSipConnection] Número inválido:', phoneNumber);
    return null;
  }

  const ddd = clean.substring(0, 2);

  if (clean.length === 10) {
    const localNumber = clean.substring(2);
    const firstDigit = localNumber[0];

    // Verifica se é celular (começa com 6, 7, 8 ou 9)
    if (['6', '7', '8', '9'].includes(firstDigit)) {
      return `+55${ddd}9${localNumber}`;
    }
    if (['2', '3', '4', '5'].includes(firstDigit)) {
      // É fixo
      return `+55${clean}`;
    }
    console.warn('[useAciSipConnection] Primeiro dígito inválido:', firstDigit);
    return null;
  }
  if (clean.length === 11) {
    const ninthDigit = clean[2];

    if (ninthDigit === '9') {
      return `+55${clean}`;
    }
    console.warn(
      '[useAciSipConnection] 11 dígitos mas não começa com 9:',
      phoneNumber
    );
    return `+55${clean}`;
  }

  return null;
}

export function useAciSipConnection() {
  /**
   * Connect to the SIP server using the current user's extension
   */
  async function connect() {
    if (client.value || isConnecting.value) {
      return true;
    }

    isConnecting.value = true;
    connectionError.value = null;

    try {
      // Fetch credentials from API
      const { data } = await AciTelephonyAPI.getMyExtension();

      if (!data.success) {
        throw new Error(data.error || 'Erro ao buscar credenciais');
      }

      credentials.value = data.credentials;

      const {
        extension,
        password,
        display_name,
        sip_server,
        ws_server,
        turn_server,
        turn_username,
        turn_password,
      } = credentials.value;

      // Create SIP client
      client.value = markRaw(
        new AciSipClient({
          extension,
          password,
          displayName: display_name,
          sipServer: sip_server,
          wsServer: ws_server,
          // TURN server configuration for NAT traversal
          turnServer: turn_server,
          turnUsername: turn_username,
          turnPassword: turn_password,
          debug: false,
          onUserAgentAction: handleUserAgentAction,
          onCallEvent: handleCallEvent,
          // Mesma fonte da faixa "Em pausa" do dialer: pausar = ficar Ocupado.
          isPaused: () =>
            store.getters.getCurrentUserAvailability === 'busy',
        })
      );

      // SIP.js usa delegates, não precisamos registrar eventos diretamente
      // O handleUserAgentAction já recebe o evento 'newRTCSession'

      await client.value.start();
      return true;
    } catch (err) {
      console.error('[useAciSipConnection] Erro ao conectar:', err.message);
      connectionError.value = err.response?.data?.error || err.message;
      isConnecting.value = false;
      return false;
    }
  }

  /**
   * Disconnect from the SIP server
   */
  function disconnect() {
    if (client.value) {
      client.value.stop();
      client.value = null;
    }
    isRegistered.value = false;
    isConnecting.value = false;
    credentials.value = null;
    resetCallState();
  }

  /**
   * Handle user agent events (registration, etc)
   */
  function handleUserAgentAction(event) {
    // Toast visual do ciclo de registro (Sonner em bottom-right) — helper
    // gerencia o toastId pra que o mesmo balão transicione loading → success/error.
    notifyRegistrationEvent(event, TOAST_LABEL);

    switch (event.type) {
      case 'registered':
        isRegistered.value = true;
        isConnecting.value = false;
        break;

      case 'unregistered':
      case 'registrationFailed':
        isRegistered.value = false;
        isConnecting.value = false;
        if (event.type === 'registrationFailed') {
          connectionError.value = 'Falha no registro SIP';
        }
        break;

      case 'disconnected':
        isRegistered.value = false;
        isConnecting.value = false;
        break;

      case 'newRTCSession':
        handleNewRTCSession(event.payload);
        break;

      case 'incomingRejectedWhilePaused':
        notifyIncomingRejectedWhilePaused(event.payload);
        break;
    }
  }

  /**
   * Handle new RTC session (incoming/outgoing call)
   * SIP.js passa o payload através do AciSipClient
   */
  function handleNewRTCSession(rtcPayload) {
    const { session } = rtcPayload;

    if (!session) {
      console.error('[useAciSipConnection] ❌ rtcPayload inválido');
      return;
    }

    // Guardar referência da sessão atual
    _state.currentSession = session;
    callDirection.value = session.direction;

    if (session.direction === 'incoming') {
      const callerNumber = session.remote_identity?.uri?.user || 'unknown';
      const callerDisplayName = session.remote_identity?.display_name || null;

      callState.value = 'ringing';
      callNumber.value = callerNumber;
      remoteDisplayName.value = callerDisplayName;

      // Bus event agora é emitido diretamente pelo AciSipClient (bypassa HMR ref scoping)
    } else {
      callState.value = 'calling';
      callNumber.value = client.value?.currentDestination || 'unknown';
      remoteDisplayName.value = null;
    }
  }

  /**
   * Make an outgoing call
   * @param {string} number - Phone number to call
   * @param {function} onCallAction - Callback for call events
   * @param {object} extraHeaders - Custom SIP headers to send (e.g., { 'Contact-ID': 10 })
   */
  function makeCall(number, onCallAction, extraHeaders = {}) {
    // Block calls during cooldown period (prevents ghost re-dial from Vue re-render)
    if (Date.now() - _state.lastCallEndedAt < MAKE_CALL_COOLDOWN_MS) {
      return;
    }

    if (!client.value || !isRegistered.value) {
      throw new Error('SIP não conectado');
    }

    const normalizedNumber = normalizePhoneOrExtension(number);

    if (!normalizedNumber) {
      throw new Error('Número de telefone inválido');
    }

    callState.value = 'calling';
    callDirection.value = 'outgoing';
    callNumber.value = normalizedNumber;

    try {
      client.value.call({
        to: normalizedNumber,
        extraHeaders: extraHeaders,
        // NÃO chamar handleCallEvent aqui — o client já o invoca via
        // params.onCallEvent dentro de emitCallEvent. Chamar nos dois lugares
        // disparava cada evento 2x (toasts duplicados). Aqui só repassamos pro
        // callback externo de quem chamou makeCall (ex.: painel).
        onCallAction: event => {
          if (onCallAction) onCallAction(event);
        },
      });
    } catch (err) {
      // Erro sincrono (ex: URI invalida no Inviter) — reseta state pra nao travar a UI
      callState.value = 'idle';
      callDirection.value = null;
      callNumber.value = null;
      throw err;
    }
  }

  /**
   * Rede de segurança: a ligação de saída foi recusada pela Meta com 403 +
   * "No Approved Call Permission Found". Dispara automaticamente o pedido de
   * permissão de chamada via WhatsApp e avisa o agente por toast.
   */
  async function handlePermissionRequired(event) {
    const waId = String(event.payload?.number || '').replace(/\D/g, '');
    if (!waId) return;

    try {
      await CallPermissionsAPI.requestPermission(waId);
      useAlert(
        'Contato ainda não autorizou chamadas. Pedido de permissão enviado via WhatsApp.'
      );
    } catch (error) {
      useAlert(
        error.response?.data?.error ||
          'Falha ao enviar o pedido de permissão de chamada.'
      );
    }
  }

  /**
   * Handle call events
   */
  function handleCallEvent(event) {
    // Rede de segurança WhatsApp: a Meta recusou a chamada por falta de permissão.
    // Tratado antes do notifyCallEvent pra não gerar toast de "falha" genérico —
    // aqui o toast é claro ("sem permissão, pedido enviado") e dispara o request.
    if (event.type === 'permission_required') {
      handlePermissionRequired(event);
      return;
    }

    // Toast da chamada em andamento (loading → success/error no mesmo balão).
    // Só pra outgoing — incoming tem ringtone + UI dedicada no dialer.
    if (callDirection.value === 'outgoing') {
      notifyCallEvent(event, `${TOAST_LABEL}: chamada`);
    }

    switch (event.type) {
      case 'incoming':
        // Já tratado em handleNewRTCSession
        break;

      case 'progress':
        if (callDirection.value === 'outgoing') {
          callState.value = 'ringing';
        }
        break;

      case 'accepted':
      case 'confirmed':
        callState.value = 'connected';
        if (!callStartTime.value) {
          callStartTime.value = new Date();
        }
        break;

      case 'ended':
        // Salva no historico antes de resetar
        saveCallToHistory();
        resetCallState();
        // Emite evento para notificar outros componentes
        emitter.emit(BUS_EVENTS.TELEPHONY_CALL_ENDED, {
          number: callNumber.value,
        });
        break;

      case 'failed':
        // Salva no historico mesmo se falhou (com duracao 0)
        saveCallToHistory();
        resetCallState();
        emitter.emit(BUS_EVENTS.TELEPHONY_CALL_ENDED, {
          number: callNumber.value,
          failed: true,
        });
        break;

      case 'hold':
        isOnHold.value = true;
        break;

      case 'unhold':
        isOnHold.value = false;
        break;

      // Consultation (attended transfer) events
      case 'consultation_calling':
        isConsultationActive.value = true;
        consultationState.value = 'calling';
        consultationTarget.value = event.target || null;
        break;

      case 'consultation_progress':
        consultationState.value = 'ringing';
        break;

      case 'consultation_established':
        consultationState.value = 'connected';
        break;

      case 'consultation_ended':
        resetConsultationState();
        break;

      case 'consultation_failed':
        resetConsultationState();
        break;

      case 'consultation_cancelled':
        resetConsultationState();
        break;

      case 'attended_transfer_completed':
        resetConsultationState();
        break;

      case 'attended_transfer_failed':
        console.error(
          '[useAciSipConnection][AttTransfer] ❌ Transferência assistida falhou:',
          event.error
        );
        break;
    }
  }

  /**
   * Answer an incoming call
   */
  function answerCall() {
    if (!client.value) return;

    // Prevent answering if not in ringing state (avoid double-click)
    if (callState.value !== 'ringing') {
      console.warn(
        '[useAciSipConnection] Chamada não está tocando, estado atual:',
        callState.value
      );
      return;
    }

    // Immediately update state to prevent UI from allowing another click
    callState.value = 'connecting';

    client.value.answer();
  }

  /**
   * Hang up the current call
   */
  function hangup() {
    if (!client.value) {
      return;
    }

    // Tela em chamada sem sessão no client (estado dessincronizado): desligar
    // tem que destravar a UI em vez de virar no-op e exigir F5.
    if (!client.value.getActiveCall()) {
      resetCallState();
      return;
    }

    try {
      client.value.hangup();

      // Força reset imediato para chamadas rejeitadas (incoming ringing)
      if (callState.value === 'ringing' && callDirection.value === 'incoming') {
        resetCallState();
      }
    } catch (e) {
      console.error('[SIP] hangup error:', e);
      resetCallState();
    }
  }

  /**
   * Toggle microphone mute
   */
  function toggleMute() {
    if (!client.value) return false;
    const muted = client.value.toggleMute();
    isMuted.value = muted;
    return muted;
  }

  /**
   * Send DTMF tone
   */
  function sendDTMF(tone) {
    if (!client.value) return;
    client.value.sendDTMF(tone);
  }

  /**
   * Toggle hold state
   */
  function toggleHold() {
    if (!client.value) return false;
    try {
      const held = client.value.toggleHold();
      isOnHold.value = held;
      return held;
    } catch (e) {
      console.error('[useAciSipConnection] Toggle hold error:', e.message);
      return isOnHold.value;
    }
  }

  /**
   * Transfer call to another number/extension
   */
  function transfer(target) {
    if (!client.value) {
      throw new Error('SIP não conectado');
    }

    // ✅ Normalizar número de destino da transferência
    const normalizedTarget = normalizePhoneOrExtension(target);

    if (!normalizedTarget) {
      throw new Error('Número de destino inválido');
    }

    client.value.transfer(normalizedTarget);
  }

  /**
   * Start attended (consultative) transfer
   * @param {string} target - Phone number or extension to consult
   */
  function attendedTransfer(target) {
    if (!client.value) {
      throw new Error('SIP não conectado');
    }

    const normalizedTarget = normalizePhoneOrExtension(target);

    if (!normalizedTarget) {
      throw new Error('Número de destino inválido');
    }

    client.value.attendedTransfer(normalizedTarget);
  }

  /**
   * Complete the attended transfer (bridge the two remote parties)
   */
  function completeAttendedTransfer() {
    if (!client.value) {
      throw new Error('SIP não conectado');
    }

    client.value.completeAttendedTransfer();
  }

  /**
   * Cancel the attended transfer and return to the original call
   */
  function cancelAttendedTransfer() {
    if (!client.value) {
      throw new Error('SIP não conectado');
    }

    client.value.cancelAttendedTransfer();
  }

  /**
   * Reset consultation state
   */
  function resetConsultationState() {
    isConsultationActive.value = false;
    consultationState.value = 'idle';
    consultationTarget.value = null;
  }

  /**
   * Reset call state
   */
  function resetCallState() {
    _state.lastCallEndedAt = Date.now();
    callState.value = 'idle';
    callDirection.value = null;
    callNumber.value = null;
    remoteDisplayName.value = null;
    callStartTime.value = null;
    isMuted.value = false;
    isOnHold.value = false;
    _state.currentSession = null;
    resetConsultationState();
  }

  /**
   * Get call duration in seconds
   */
  function getCallDuration() {
    if (!callStartTime.value) return 0;
    return Math.floor((new Date() - callStartTime.value) / 1000);
  }

  /**
   * Format duration as MM:SS
   */
  function formatDuration(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  // Computed properties
  const isConnected = computed(() => isRegistered.value);
  const hasActiveCall = computed(() =>
    ['calling', 'ringing', 'connecting', 'connected'].includes(callState.value)
  );
  const isInCall = computed(() => callState.value === 'connected');

  // Cleanup on unmount
  onUnmounted(() => {
    // Don't disconnect - keep connection alive across components
  });

  // HMR: if client already exists (created by a previous module version),
  // update its callbacks to reference the current module's functions.
  // This ensures the existing AciSipClient uses the same HMR-safe refs.
  if (client.value) {
    client.value.onUserAgentAction = handleUserAgentAction;
    client.value.params.onCallEvent = handleCallEvent;
  }

  return {
    // State
    client,
    isRegistered,
    isConnecting,
    isConnected,
    credentials,
    connectionError,

    // Call state
    callState,
    callDirection,
    callNumber,
    remoteDisplayName,
    callStartTime,
    isMuted,
    isOnHold,
    hasActiveCall,
    isInCall,

    // Consultation (attended transfer) state
    isConsultationActive,
    consultationState,
    consultationTarget,

    // Methods
    connect,
    disconnect,
    makeCall,
    answerCall,
    hangup,
    toggleMute,
    toggleHold,
    transfer,
    attendedTransfer,
    completeAttendedTransfer,
    cancelAttendedTransfer,
    sendDTMF,
    getCallDuration,
    formatDuration,

    // Call metadata (para historico)
    callMetadata,
    setCallMetadata,

    // Audio unlock (must be called from user interaction)
    unlockAudio: () => client.value?.unlockAudio(),
  };
}
